import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Check, AlertTriangle, ArrowUp, RefreshCw, XCircle, Zap, Shield, ChevronRight, Target, CreditCard, ExternalLink, ShieldCheck, Gem } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../utils/authFetch';
import { toast } from 'sonner';

const ADMIN_EMAILS = ['info@flence.com', 'admin@fashionspyder.com'];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };
const MONO  = { fontFamily: "'IBM Plex Mono', monospace" };

// ── Countdown hook ──────────────────────────────────────────────────────────
const useCountdown = (expiresAt) => {
  const calc = () => {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      expired: false,
      days:    Math.floor(diff / 86400000),
      hours:   Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [remaining, setRemaining] = useState(calc);
  useEffect(() => {
    const t = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]); // eslint-disable-line
  return remaining;
};

// ── Billing period progress ─────────────────────────────────────────────────
const BillingProgress = ({ startedAt, expiresAt }) => {
  if (!startedAt || !expiresAt) return null;
  const total = new Date(expiresAt) - new Date(startedAt);
  const used  = new Date() - new Date(startedAt);
  const pct   = Math.min(100, Math.max(0, Math.round((used / total) * 100)));
  const daysLeft = Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 86400000));
  return (
    <div className="mt-6 pt-6 border-t border-white/5">
      <div className="flex items-center justify-between mb-2">
        <span style={{ ...MONO, fontSize: '8px', letterSpacing: '0.14em' }} className="text-white/20 uppercase">Billing period</span>
        <span style={{ ...MONO, fontSize: '8px' }} className="text-white/20">{daysLeft} days left</span>
      </div>
      <div className="h-px bg-white/8 relative">
        <div className="absolute inset-y-0 left-0 bg-white/30 transition-all duration-1000" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span style={{ ...MONO, fontSize: '8px' }} className="text-white/15">
          {new Date(startedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </span>
        <span style={{ ...MONO, fontSize: '8px' }} className="text-white/15">
          {new Date(expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      </div>
    </div>
  );
};

// ── Cancel modal ─────────────────────────────────────────────────────────────
const CancelModal = ({ plan, onConfirm, onClose, loading }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
    <div className="bg-white max-w-sm w-full p-7 space-y-5 border border-border">
      <div>
        <h3 style={SERIF} className="text-xl font-semibold mb-1">Cancel {plan?.toUpperCase()}?</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Your access continues until the current period ends. Renew anytime.
        </p>
      </div>
      <div className="flex gap-3">
        <Button onClick={onConfirm} disabled={loading} className="flex-1 rounded-none bg-red-600 hover:bg-red-700 text-white">
          {loading ? 'Cancelling...' : 'Yes, cancel'}
        </Button>
        <Button onClick={onClose} variant="outline" className="flex-1 rounded-none">Keep plan</Button>
      </div>
    </div>
  </div>
);

// ── Feature row ──────────────────────────────────────────────────────────────
const FeatureRow = ({ label, included, locked, onUpgrade }) => (
  <div className={`flex items-center justify-between py-2.5 border-b border-border last:border-0 ${locked ? 'opacity-40' : ''}`}>
    <div className="flex items-center gap-2.5">
      <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${included ? 'bg-foreground' : 'border border-border'}`}>
        {included && <Check className="w-2.5 h-2.5 text-background" strokeWidth={2.5} />}
      </div>
      <span className="text-sm">{label}</span>
    </div>
    {locked && (
      <button onClick={onUpgrade} style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
        className="text-muted-foreground border border-border px-2 py-0.5 uppercase hover:border-foreground hover:text-foreground transition-colors">
        INTELLIGENCE
      </button>
    )}
  </div>
);

const SectionLabel = ({ children }) => (
  <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.18em' }} className="uppercase text-muted-foreground mb-4">{children}</p>
);

// ── Main ─────────────────────────────────────────────────────────────────────
const Account = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [rolesInput, setRolesInput] = useState('');
  const [locationsInput, setLocationsInput] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  const hasPreferences = (user?.preferred_roles?.length || 0) > 0 || (user?.preferred_locations?.length || 0) > 0;
  const [profileOpen, setProfileOpen] = useState(false);

  // Auto-open profile if hiring preferences not yet set
  useEffect(() => {
    if (user && !hasPreferences) setProfileOpen(true);
  }, [user]); // eslint-disable-line

  const countdown = useCountdown(user?.subscription_expires_at);
  const [dedicatedSub, setDedicatedSub] = useState(null);

  useEffect(() => {
    authFetch(`${BACKEND}/api/dedicated/status`)
      .then(r => r.json())
      .then(d => { if (d.success && d.subscription?.status === 'active') setDedicatedSub(d.subscription); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || '',
        company_name: user.company_name || '',
        company_website: user.company_website || '',
        company_logo_url: user.company_logo_url || '',
        company_linkedin: user.company_linkedin || '',
        job_title: user.job_title || '',
        team_size: user.team_size || '',
        notes: user.notes || '',
      });
      setRolesInput((user.preferred_roles || []).join(', '));
      setLocationsInput((user.preferred_locations || []).join(', '));
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      preferred_roles: rolesInput.split(',').map(s => s.trim()).filter(Boolean),
      preferred_locations: locationsInput.split(',').map(s => s.trim()).filter(Boolean),
    };
    try {
      const res = await authFetch(`${BACKEND}/api/dashboard/profile`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to save');
      await checkAuth();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast.success('Profile saved');
    } catch {
      toast.error('Could not save profile. Please try again.');
    } finally { setSaving(false); }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const res = await authFetch(`${BACKEND}/api/payments/cancel`, { method: 'POST' });
      const data = await res.json();
      if (data.success) { await checkAuth(); setShowCancel(false); setActionMsg('Plan cancelled. Access continues until expiry.'); }
    } catch {} finally { setCancelLoading(false); }
  };

  const handleRenewOrUpgrade = async (planId) => {
    setActionLoading(planId);
    setActionMsg('');
    try {
      const res = await authFetch(`${BACKEND}/api/payments/renew`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, origin: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setActionMsg(data.detail || 'Error starting checkout.');
    } catch { setActionMsg('Error. Please try again.'); }
    finally { setActionLoading(null); }
  };

  const handleBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await authFetch(`${BACKEND}/api/payments/billing-portal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else toast.error(data.detail || 'Could not open billing portal.');
    } catch { toast.error('Error opening billing portal.'); }
    finally { setPortalLoading(false); }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const plan        = user?.subscription_plan || 'free';
  const status      = user?.subscription_status;
  const expiresAt   = user?.subscription_expires_at;
  const startedAt   = user?.subscription_started_at;
  const isFree      = plan === 'free';
  const isRecon     = plan === 'recon';
  const isIntelligence = plan === 'intelligence';
  const isActive    = status === 'active';
  const isCancelled = status === 'cancelled';
  const isPaymentFailed = status === 'payment_failed';
  const isExpired   = countdown?.expired;
  const isUrgent    = countdown && !countdown.expired && countdown.days < 7;
  const hasStripeCustomer = !!user?.stripe_customer_id;
  const isAdmin     = ADMIN_EMAILS.includes(user?.email?.toLowerCase());
  const memberId    = user?.id ? user.id.slice(-8).toUpperCase() : '—';
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : '—';

  const statusLabel = isExpired ? 'Expired' : isCancelled ? 'Cancelled' : isUrgent ? 'Expiring soon' : isActive ? 'Active' : 'Inactive';

  const RECON_FEATURES = ['Full talent gallery', 'Basic filters (role, seniority, city)', 'Complete candidate profiles', 'Unlimited pipeline & Kanban', 'Bulk add candidates', 'HR dashboard & analytics'];
  const INTELLIGENCE_ONLY = ['AI DNA Intelligence Panel', 'AI Interviewer — concierge chat', 'AI Matchmaker — role-fit ranking', 'AI Talent Brief — semantic search', 'Advanced filters (country, availability, work mode, exp)', 'Find Similar profiles', 'Compare up to 3 candidates', 'Priority support'];

  // Plan-specific accent — sophisticated: only the details/fonts change colour, not the card.
  // free = grey · recon = blue · intelligence = red · dedicated = gold
  const planAccent = isIntelligence
    ? { hex: '#E5484D', soft: 'rgba(229,72,77,0.16)', Icon: Zap, tag: 'Premium Intelligence' }
    : isRecon
    ? { hex: '#5B8DEF', soft: 'rgba(91,141,239,0.16)', Icon: Shield, tag: 'Core Access' }
    : { hex: '#9CA3AF', soft: 'rgba(255,255,255,0.06)', Icon: null, tag: 'No active plan' };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="account-page">
      {showCancel && <CancelModal plan={plan} onConfirm={handleCancel} onClose={() => setShowCancel(false)} loading={cancelLoading} />}

      {/* Page header */}
      <div>
        <h1 style={{ ...SERIF, fontSize: '2rem', lineHeight: 1.1 }} className="font-semibold text-foreground">Membership</h1>
        <p style={{ ...MONO, fontSize: '10px', letterSpacing: '0.15em' }} className="text-muted-foreground uppercase mt-1">
          {user?.email} · Since {memberSince}
        </p>
      </div>

      {/* ── DEDICATED HIRING — gold active banner ── */}
      {dedicatedSub && (
        <div
          className="relative overflow-hidden p-5 flex items-center gap-4"
          data-testid="dedicated-membership-banner"
          style={{ background: 'linear-gradient(135deg, #1a1408 0%, #2a2008 100%)', borderLeft: '3px solid #c9a84c' }}
        >
          <span className="w-10 h-10 flex items-center justify-center rounded-sm flex-shrink-0" style={{ background: 'rgba(201,168,76,0.16)' }}>
            <Gem className="w-5 h-5" style={{ color: '#c9a84c' }} strokeWidth={1.5} />
          </span>
          <div className="flex-1">
            <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.18em', color: '#c9a84c' }} className="uppercase mb-1">
              Dedicated Hiring · Active
            </p>
            <p style={{ ...SERIF }} className="text-lg text-white font-medium leading-tight">
              Your dedicated talent search is live
            </p>
            <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
              €{Number(dedicatedSub.monthly_amount || dedicatedSub.monthly_fee || 0).toLocaleString()}/month · auto-renewing
            </p>
          </div>
          <button onClick={() => navigate('/dashboard/dedicated')}
            style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em', color: '#c9a84c', borderColor: 'rgba(201,168,76,0.4)' }}
            className="border px-3 py-2 uppercase hover:bg-[rgba(201,168,76,0.1)] transition-colors flex-shrink-0"
            data-testid="dedicated-banner-manage">
            Manage →
          </button>
        </div>
      )}

      {/* ── MEMBERSHIP CARD — sleek monochrome ── */}
      <div className="bg-[#111111]" data-testid="membership-card" style={{ borderTop: `3px solid ${isFree ? 'transparent' : planAccent.hex}` }}>
        <div className="p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* Left: Plan identity */}
            <div className="flex flex-col justify-between min-h-[160px]">
              <div>
                <div className="flex items-center gap-2.5 mb-5">
                  {planAccent.Icon && (
                    <span className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ background: planAccent.soft }}>
                      <planAccent.Icon className="w-3.5 h-3.5" style={{ color: planAccent.hex }} strokeWidth={2} />
                    </span>
                  )}
                  <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.22em' }} className="text-white/40 uppercase">
                    FashionSpyder · {planAccent.tag}
                  </p>
                </div>
                <h2
                  style={{ ...SERIF, fontSize: 'clamp(2.8rem, 5vw, 4.2rem)', lineHeight: 0.92, letterSpacing: '-0.02em' }}
                  className="text-white font-medium mb-3" data-testid="plan-name">
                  {isFree ? 'Free' : isRecon ? 'Recon' : 'Intelligence'}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  {!isFree && (
                    <p style={{ ...MONO, fontSize: '11px', letterSpacing: '0.05em' }} className="text-white/50">
                      {isRecon ? '€149' : '€349'}/mo
                    </p>
                  )}
                  <span
                    data-testid="membership-status-pill"
                    style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em',
                      background: isActive && !isExpired ? planAccent.soft : 'rgba(255,255,255,0.05)',
                      color: isExpired ? '#f87171' : isUrgent ? '#fbbf24' : (isActive && !isFree) ? planAccent.hex : 'rgba(255,255,255,0.45)' }}
                    className="uppercase px-2.5 py-1 inline-flex items-center gap-1.5 rounded-sm">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: isExpired ? '#f87171' : isUrgent ? '#fbbf24' : (isActive && !isFree) ? planAccent.hex : 'rgba(255,255,255,0.3)' }} />
                    {statusLabel}
                  </span>
                </div>
              </div>
              <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }} className="text-white/25 mt-6">
                FS–{memberId}
              </p>
            </div>

            {/* Right: Countdown + progress */}
            <div className="flex flex-col justify-center">
              {!isFree && expiresAt && countdown && !isExpired && (
                <div>
                  <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.15em' }} className="text-white/20 uppercase mb-4">
                    {isCancelled ? 'Access until' : 'Next renewal'}
                  </p>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { v: countdown.days,    l: 'Days' },
                      { v: countdown.hours,   l: 'Hrs' },
                      { v: countdown.minutes, l: 'Min' },
                      { v: countdown.seconds, l: 'Sec' },
                    ].map(({ v, l }) => (
                      <div key={l}>
                        <p style={{ ...SERIF, fontSize: '2.4rem', lineHeight: 1 }}
                          className={`tabular-nums font-light ${isUrgent ? 'text-red-300/70' : 'text-white/70'}`}>
                          {String(v).padStart(2, '0')}
                        </p>
                        <p style={{ ...MONO, fontSize: '8px', letterSpacing: '0.1em' }} className="text-white/18 uppercase mt-1">{l}</p>
                      </div>
                    ))}
                  </div>
                  <BillingProgress startedAt={startedAt} expiresAt={expiresAt} />
                </div>
              )}
              {!isFree && isExpired && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400/60 flex-shrink-0" strokeWidth={1.5} />
                  <p className="text-sm text-red-300/60">Subscription expired. Renew to restore access.</p>
                </div>
              )}
              {isFree && (
                <button onClick={() => navigate('/pricing')}
                  style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                  className="self-start text-white/25 border border-white/8 px-3 py-2 uppercase hover:text-white/50 hover:border-white/20 transition-colors">
                  View plans →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action message */}
      {actionMsg && (
        <div className="p-4 border border-green-200 bg-green-50 text-green-700 text-sm flex items-center gap-2">
          <Check className="w-4 h-4 flex-shrink-0" />{actionMsg}
        </div>
      )}

      {/* ── HIRING PROFILE — above plan features ── */}
      <div className="bg-white border border-border" data-testid="profile-section">
        <button
          onClick={() => setProfileOpen(!profileOpen)}
          className="w-full flex items-center justify-between p-6 hover:bg-secondary/20 transition-colors"
          data-testid="profile-toggle">
          <div className="flex items-start gap-3">
            <Target className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <div className="text-left">
              <p className="font-semibold text-sm">Hiring Profile</p>
              <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }} className="text-muted-foreground uppercase mt-0.5">
                {hasPreferences
                  ? `${(user?.preferred_roles || []).slice(0, 2).join(', ')}${(user?.preferred_roles?.length || 0) > 2 ? ' +more' : ''}`
                  : 'Not set — set your focus to unlock personalised matches'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!hasPreferences && (
              <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
                className="text-amber-600 border border-amber-200 bg-amber-50 px-2 py-0.5 uppercase">Set up</span>
            )}
            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${profileOpen ? 'rotate-90' : ''}`} strokeWidth={1.5} />
          </div>
        </button>

        {profileOpen && (
          <form onSubmit={handleSave} className="px-6 pb-6 border-t border-border pt-5 space-y-5">
            {!hasPreferences && (
              <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
                <Target className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                Set your preferred roles and locations — FashionSpyder will highlight matching candidates in the gallery.
              </div>
            )}

            <div className="space-y-4">
              <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }} className="text-muted-foreground uppercase">Hiring Focus</p>
              <div>
                <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">
                  Target Roles <span className="text-amber-600">·</span> comma separated
                </label>
                <Input value={rolesInput} onChange={e => setRolesInput(e.target.value)}
                  placeholder="Fashion Designer, Buyer, Creative Director" className="rounded-none h-9" />
              </div>
              <div>
                <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">
                  Target Locations <span className="text-amber-600">·</span> comma separated
                </label>
                <Input value={locationsInput} onChange={e => setLocationsInput(e.target.value)}
                  placeholder="Milan, Paris, London" className="rounded-none h-9" />
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }} className="text-muted-foreground uppercase">Profile</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">Full Name</label>
                  <Input value={form.full_name || ''} onChange={f('full_name')} className="rounded-none h-9" />
                </div>
                <div>
                  <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">Job Title</label>
                  <Input value={form.job_title || ''} onChange={f('job_title')} placeholder="HR Director" className="rounded-none h-9" />
                </div>
                <div>
                  <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">Company</label>
                  <Input value={form.company_name || ''} onChange={f('company_name')} className="rounded-none h-9" />
                </div>
                <div>
                  <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">Website</label>
                  <Input value={form.company_website || ''} onChange={f('company_website')} placeholder="https://" className="rounded-none h-9" />
                </div>
                <div>
                  <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">Team Size</label>
                  <select value={form.team_size || ''} onChange={f('team_size')}
                    className="w-full border border-border px-3 py-2 text-sm bg-white rounded-none h-9">
                    <option value="">Select...</option>
                    {['1-5', '6-20', '21-50', '51-200', '200+'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Flence credentialing — shown to candidates on Flence when HR expresses interest */}
              <div className="border-t border-border pt-4">
                <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.15em' }} className="text-muted-foreground uppercase mb-3">
                  Flence Credentialing <span className="normal-case tracking-normal text-muted-foreground/60 font-normal">· shown to candidates when you express interest</span>
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">Company Logo URL</label>
                    <Input value={form.company_logo_url || ''} onChange={f('company_logo_url')} placeholder="https://yourcompany.com/logo.png" className="rounded-none h-9" data-testid="company-logo-input" />
                  </div>
                  <div>
                    <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">LinkedIn Page</label>
                    <Input value={form.company_linkedin || ''} onChange={f('company_linkedin')} placeholder="https://linkedin.com/company/..." className="rounded-none h-9" data-testid="company-linkedin-input" />
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" disabled={saving}
              className={`rounded-none gap-2 h-9 text-sm ${saved ? 'bg-green-600 hover:bg-green-600 text-white' : 'bg-foreground text-background hover:bg-foreground/80'}`}
              data-testid="save-profile-btn">
              {saved ? <><Check className="w-3.5 h-3.5" /> Saved</> : saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </form>
        )}
      </div>

      {/* ── SUBSCRIPTION ACTIONS ── */}
      {!isFree && (
        <div className="bg-white border border-border p-6 space-y-5" data-testid="subscription-actions">
          <SectionLabel>Manage Subscription</SectionLabel>

          {/* Payment failed banner */}
          {isPaymentFailed && (
            <div className="p-4 border border-red-200 bg-red-50 flex items-start gap-3" data-testid="payment-failed-banner">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Payment failed</p>
                <p className="text-xs text-red-600 mt-0.5">We couldn't process your last payment. Update your payment method to keep access.</p>
              </div>
              <Button onClick={handleBillingPortal} disabled={portalLoading}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white gap-1.5 text-xs h-8 shrink-0"
                data-testid="fix-payment-btn">
                <CreditCard className="w-3 h-3" strokeWidth={1.5} />
                {portalLoading ? 'Loading...' : 'Fix payment'}
              </Button>
            </div>
          )}

          <div className="space-y-4">
            {(isExpired || isCancelled || isUrgent) && (
              <div className="space-y-2">
                <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="text-muted-foreground uppercase">
                  Renew {plan.toUpperCase()}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={() => handleRenewOrUpgrade(plan)} disabled={actionLoading !== null}
                    className="rounded-none bg-foreground text-background hover:bg-foreground/80 gap-2 text-sm h-10"
                    data-testid="renew-monthly-btn">
                    <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {actionLoading === plan ? 'Loading...' : `Monthly — €${isRecon ? '149' : '349'}/mo`}
                  </Button>
                  <Button onClick={() => handleRenewOrUpgrade(plan + '_annual')} disabled={actionLoading !== null}
                    variant="outline" className="rounded-none border-green-400 text-green-700 hover:bg-green-50 gap-2 text-sm h-10"
                    data-testid="renew-annual-btn">
                    <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {actionLoading === plan + '_annual' ? 'Loading...' : `Annual — €${isRecon ? '1,252' : '2,930'}/yr · Save €${isRecon ? '536' : '1,258'}`}
                  </Button>
                </div>
              </div>
            )}
            {isRecon && !isExpired && (
              <div className="space-y-2 border-t border-border pt-4">
                <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="text-muted-foreground uppercase">Upgrade to Intelligence</p>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={() => handleRenewOrUpgrade('intelligence')} disabled={actionLoading !== null}
                    className="rounded-none bg-foreground text-background hover:bg-foreground/80 gap-2 text-sm h-10"
                    data-testid="upgrade-monthly-btn">
                    <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {actionLoading === 'intelligence' ? 'Loading...' : 'Monthly — €349/mo'}
                  </Button>
                  <Button onClick={() => handleRenewOrUpgrade('intelligence_annual')} disabled={actionLoading !== null}
                    variant="outline" className="rounded-none gap-2 text-sm h-10"
                    data-testid="upgrade-annual-btn">
                    <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {actionLoading === 'intelligence_annual' ? 'Loading...' : 'Annual — €2,930/yr · Save €1,258'}
                  </Button>
                </div>
              </div>
            )}

            {/* Stripe Billing Portal — manage payment method, invoices */}
            {hasStripeCustomer && (
              <div className={`${(isRecon && !isExpired) || (isExpired || isCancelled || isUrgent) ? 'border-t border-border pt-4' : ''} flex items-center justify-between gap-4`}
                data-testid="billing-portal-section">
                <div>
                  <p className="text-sm font-medium">Billing & Invoices</p>
                  <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }} className="text-muted-foreground mt-0.5">
                    Manage payment method, download invoices
                  </p>
                </div>
                <Button onClick={handleBillingPortal} disabled={portalLoading}
                  variant="outline" className="rounded-none gap-1.5 text-sm h-9 shrink-0"
                  data-testid="billing-portal-btn">
                  <CreditCard className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {portalLoading ? 'Loading...' : 'Manage Billing'}
                  <ExternalLink className="w-3 h-3 ml-0.5" strokeWidth={1.5} />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {isFree && !isAdmin && (
        <div className="bg-[#111111] p-6 flex items-center justify-between" data-testid="free-upgrade-banner">
          <div>
            <p style={SERIF} className="text-white text-base font-medium mb-1">Unlock the full platform</p>
            <p style={{ ...MONO, fontSize: '10px', letterSpacing: '0.08em' }} className="text-white/30">
              RECON from €149/mo · INTELLIGENCE from €349/mo
            </p>
          </div>
          <Button onClick={() => navigate('/pricing')} className="rounded-none bg-white text-black hover:bg-white/90 gap-2 text-sm shrink-0">
            View Plans <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {isFree && isAdmin && (
        <div className="bg-[#111111] p-6 flex items-center gap-4" data-testid="admin-account-banner">
          <ShieldCheck className="w-5 h-5 text-white/40 flex-shrink-0" strokeWidth={1.5} />
          <div>
            <p style={SERIF} className="text-white text-base font-medium mb-0.5">Admin account</p>
            <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }} className="text-white/30 uppercase">
              Full platform access · Subscription management not applicable
            </p>
          </div>
        </div>
      )}

      {/* ── PLAN FEATURES ── */}
      <div className="bg-white border border-border p-6" data-testid="plan-features">
        <SectionLabel>What's included</SectionLabel>
        <div className={isRecon ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : ''}>
          <div>
            {isIntelligence && (
              <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }} className="text-muted-foreground uppercase mb-3 flex items-center gap-1.5">
                <Shield className="w-3 h-3" /> Recon
              </p>
            )}
            {RECON_FEATURES.map(feat => <FeatureRow key={feat} label={feat} included={!isFree} locked={false} />)}
          </div>
          {(isIntelligence || isRecon) && (
            <div>
              <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }}
                className={`uppercase mb-3 flex items-center gap-1.5 ${isRecon ? 'text-muted-foreground/40' : 'text-muted-foreground'}`}>
                <Zap className="w-3 h-3" /> {isRecon ? 'Intelligence — locked' : 'Intelligence'}
              </p>
              {INTELLIGENCE_ONLY.map(feat => (
                <FeatureRow key={feat} label={feat} included={isIntelligence} locked={isRecon} onUpgrade={() => navigate('/pricing')} />
              ))}
            </div>
          )}
        </div>
        {isRecon && !isExpired && (
          <div className="mt-5 border-t border-border pt-5 flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">Get the full AI stack with Intelligence.</p>
            <Button onClick={() => navigate('/pricing')} className="rounded-none bg-foreground text-background hover:bg-foreground/80 gap-1.5 text-sm shrink-0">
              <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Upgrade
            </Button>
          </div>
        )}
      </div>

      {/* ── ACCOUNT INFO ── */}
      <div className="bg-white border border-border p-6" data-testid="account-info">
        <SectionLabel>Account Info</SectionLabel>
        <div className="space-y-0">
          {[
            { k: 'Email',         v: user?.email },
            { k: 'Auth Method',   v: user?.auth_provider },
            { k: 'Verified',      v: user?.email_verified ? 'Yes' : 'No', accent: user?.email_verified ? 'text-green-600' : 'text-red-500' },
            { k: 'Member Since',  v: user?.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
            expiresAt && { k: 'Expires', v: new Date(expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }), accent: isUrgent ? 'text-yellow-700 font-medium' : '' },
          ].filter(Boolean).map(({ k, v, accent }) => (
            <div key={k} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
              <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.1em' }} className="text-muted-foreground uppercase">{k}</span>
              <span className={`text-sm font-medium ${accent || ''}`}>{v || '—'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── MANAGE SUBSCRIPTION (bottom) ── */}
      {isActive && !isExpired && !isFree && !isAdmin && (
        <div className="bg-white border border-border p-6" data-testid="manage-subscription">
          <SectionLabel>Manage Subscription</SectionLabel>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                {isCancelled ? 'Auto-renewal is off' : 'Renews automatically every month'}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-md">
                {isCancelled
                  ? `Your ${plan.toUpperCase()} access stays active until ${expiresAt ? new Date(expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'the period ends'}, then it won't renew.`
                  : `Your ${plan.toUpperCase()} plan renews automatically each month until you cancel. Cancelling keeps your access until the end of the current billing period.`}
              </p>
            </div>
            {!isCancelled && (
              <Button onClick={() => setShowCancel(true)} variant="outline"
                className="rounded-none border-border text-muted-foreground hover:text-red-600 hover:border-red-300 hover:bg-red-50 gap-2 text-sm shrink-0"
                data-testid="cancel-plan-btn">
                <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                Cancel subscription
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
